// ---------------------------------------------------------------------------
// CreateDatasetDialog — Modal form for creating a new dataset.
//
// Renders: info banner, Dataset Name input, Access Level card-toggle,
// Data Type dropdown, and Create/Cancel footer.
//
// The caller provides onSubmit (async API call) and onCreated (refresh
// callback). The dialog owns its own loading/error state.
// ---------------------------------------------------------------------------

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Label,
  MessageBar,
  MessageBarBody,
  Select,
  Textarea,
  mergeClasses,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import { isAccessDeniedError } from '../../../api/errors.js';
import {
  DATASET_CREATE_DATA_TYPES,
  DATASET_RETENTION_DEFAULT,
  DATASET_RETENTION_OPTIONS,
  DATASET_RESTRICTED_SENSITIVITY_LABELS,
  DATASET_USAGE_RESTRICTION_OPTIONS,
} from '../../../constants/index.js';
import type {
  DatasetAccessLevel,
  DatasetCreateDataType,
  DatasetRestrictedSensitivityLabel,
  DatasetRetentionOption,
  DatasetUsageRestriction,
} from '../../../services/dataHubService.interface.js';
import { DatalakeAccessDenied } from '../DatalakeAccessDenied/DatalakeAccessDenied.js';

import type { CreateDatasetDialogProps } from './CreateDatasetDialog.interface.js';
import { useStyles } from './CreateDatasetDialog.style.js';

const NAME_REQUIRED_ERROR = 'Dataset name is required.';
const DATA_TYPE_REQUIRED_ERROR = 'Data type is required.';
const SENSITIVITY_LABEL_REQUIRED_ERROR = 'Sensitivity label is required.';
const SOURCE_PROPERTIES_INVALID_JSON_ERROR = 'Invalid JSON.';

interface SelectCardOptionProps {
  selected: boolean;
  title: string;
  subtitle?: string;
  onClick: () => void;
  disabled?: boolean;
}

function SelectCardOption({
  selected,
  title,
  subtitle,
  onClick,
  disabled,
}: SelectCardOptionProps) {
  const styles = useStyles();

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={mergeClasses(
        styles.accessCard,
        selected && styles.accessCardSelected,
      )}
      onClick={onClick}
      disabled={disabled}
    >
      <span className={styles.accessCardTitle}>{title}</span>
      {subtitle !== undefined && (
        <span className={styles.accessCardSubtitle}>{subtitle}</span>
      )}
    </button>
  );
}

export function CreateDatasetDialog({
  open,
  onClose,
  onSubmit,
  onCreated,
}: CreateDatasetDialogProps) {
  const styles = useStyles();

  const [name, setName] = useState('');
  const [accessCategory, setAccessCategory] =
    useState<DatasetAccessLevel>('Public');
  const [dataType, setDataType] = useState<DatasetCreateDataType | undefined>(
    undefined,
  );
  const [sensitivityLabel, setSensitivityLabel] = useState<
    DatasetRestrictedSensitivityLabel | undefined
  >(undefined);
  const [usageRestriction, setUsageRestriction] = useState<
    DatasetUsageRestriction | ''
  >('');
  const [subType, setSubType] = useState('');
  const [source, setSource] = useState('');
  const [retentionOption, setRetentionOption] =
    useState<DatasetRetentionOption>(DATASET_RETENTION_DEFAULT);
  const [sourceProperties, setSourceProperties] = useState('{}');
  const [sourcePropertiesError, setSourcePropertiesError] = useState<
    string | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ReactNode>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [dataTypeTouched, setDataTypeTouched] = useState(false);

  const nameError =
    nameTouched && name.trim().length === 0 ? NAME_REQUIRED_ERROR : null;
  const dataTypeError =
    dataTypeTouched && dataType === undefined ? DATA_TYPE_REQUIRED_ERROR : null;
  const sensitivityLabelError =
    accessCategory === 'Restricted' && sensitivityLabel === undefined
      ? SENSITIVITY_LABEL_REQUIRED_ERROR
      : null;

  const isValid =
    name.trim().length > 0 &&
    dataType !== undefined &&
    (accessCategory === 'Public' || sensitivityLabel !== undefined);

  const resetForm = useCallback(() => {
    setName('');
    setAccessCategory('Public');
    setDataType(undefined);
    setSensitivityLabel(undefined);
    setUsageRestriction('');
    setSubType('');
    setSource('');
    setRetentionOption(DATASET_RETENTION_DEFAULT);
    setSourceProperties('{}');
    setSourcePropertiesError(null);
    setSubmitError(null);
    setNameTouched(false);
    setDataTypeTouched(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!isValid || dataType === undefined) return;

    const resolvedLabel =
      accessCategory === 'Public' ? ('general' as const) : sensitivityLabel;
    if (resolvedLabel === undefined) return;

    let parsedSourceProperties: Record<string, unknown> | null = null;
    const trimmedProps = sourceProperties.trim();
    if (trimmedProps !== '' && trimmedProps !== '{}') {
      try {
        const parsed: unknown = JSON.parse(trimmedProps);
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setSourcePropertiesError(SOURCE_PROPERTIES_INVALID_JSON_ERROR);
          return;
        }
        parsedSourceProperties = parsed as Record<string, unknown>;
      } catch {
        setSourcePropertiesError(SOURCE_PROPERTIES_INVALID_JSON_ERROR);
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    const resolvedRestriction: DatasetUsageRestriction | undefined =
      usageRestriction || undefined;
    const resolvedRetention: DatasetRetentionOption | undefined =
      retentionOption !== DATASET_RETENTION_DEFAULT
        ? retentionOption
        : undefined;

    try {
      const dataset = await onSubmit({
        name: name.trim(),
        accessCategory,
        dataType,
        sensitivityLabel: resolvedLabel,
        usageRestriction: resolvedRestriction,
        subType: subType.trim() || undefined,
        source: source.trim() || undefined,
        retentionOption: resolvedRetention,
        sourceProperties: parsedSourceProperties,
      });
      onCreated?.(dataset);
      resetForm();
      onClose();
    } catch (err) {
      setSubmitError(
        isAccessDeniedError(err) ? (
          <DatalakeAccessDenied action="create datasets" hideTitle />
        ) : err instanceof Error ? (
          err.message
        ) : (
          'Failed to create dataset.'
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isValid,
    dataType,
    name,
    accessCategory,
    sensitivityLabel,
    usageRestriction,
    subType,
    source,
    retentionOption,
    sourceProperties,
    onSubmit,
    onCreated,
    resetForm,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Create Dataset</DialogTitle>

          <DialogContent>
            <div className={styles.form}>
              {/* Owner info banner */}
              <div className={styles.infoBanner} role="note">
                <Info16Regular aria-hidden="true" />
                <span>You become the initial Owner of this dataset.</span>
              </div>

              {/* Dataset Name */}
              <Field
                label="Dataset Name"
                required
                validationState={nameError ? 'error' : 'none'}
                validationMessage={nameError ?? undefined}
              >
                <Input
                  id="create-dataset-name"
                  className={styles.input}
                  placeholder="Enter dataset name..."
                  autoComplete="off"
                  value={name}
                  onChange={(_e, data) => {
                    setName(data.value);
                  }}
                  onBlur={() => setNameTouched(true)}
                  disabled={isSubmitting}
                />
              </Field>

              {/* Access Level */}
              <div className={styles.fieldGroup}>
                <Label required>Access Level</Label>
                <div
                  className={styles.accessLevelGroup}
                  role="radiogroup"
                  aria-label="Access Level"
                >
                  <SelectCardOption
                    selected={accessCategory === 'Public'}
                    title="Public"
                    subtitle="Tenant-wide access"
                    onClick={() => {
                      setAccessCategory('Public');
                      setSensitivityLabel(undefined);
                    }}
                    disabled={isSubmitting}
                  />
                  <SelectCardOption
                    selected={accessCategory === 'Restricted'}
                    title="Restricted"
                    subtitle="Managed via security groups"
                    onClick={() => setAccessCategory('Restricted')}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Sensitivity Label — only shown when Restricted */}
              {accessCategory === 'Restricted' && (
                <div className={styles.fieldGroup}>
                  <Label required>Sensitivity Label</Label>
                  <div
                    className={styles.accessLevelGroup}
                    role="radiogroup"
                    aria-label="Sensitivity Label"
                  >
                    {DATASET_RESTRICTED_SENSITIVITY_LABELS.map(
                      ({ label, value }) => (
                        <SelectCardOption
                          key={value}
                          selected={sensitivityLabel === value}
                          title={label}
                          onClick={() => setSensitivityLabel(value)}
                          disabled={isSubmitting}
                        />
                      ),
                    )}
                  </div>
                  {sensitivityLabelError !== null && (
                    <span className={styles.fieldError} role="alert">
                      {sensitivityLabelError}
                    </span>
                  )}
                </div>
              )}

              {/* Data Type */}
              <Field
                label="Data Type"
                required
                validationState={dataTypeError ? 'error' : 'none'}
                validationMessage={dataTypeError ?? undefined}
              >
                <Select
                  aria-label="Data Type"
                  className={styles.dropdown}
                  value={dataType ?? ''}
                  onChange={(_e, data) => {
                    setDataTypeTouched(true);
                    setDataType(
                      data.value !== ''
                        ? (data.value as DatasetCreateDataType)
                        : undefined,
                    );
                  }}
                  onBlur={() => setDataTypeTouched(true)}
                  disabled={isSubmitting}
                >
                  <option value="" disabled>
                    — select type —
                  </option>
                  {DATASET_CREATE_DATA_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Usage Restriction */}
              <Field label="Usage Restriction">
                <Select
                  aria-label="Usage Restriction"
                  className={styles.dropdown}
                  value={usageRestriction}
                  onChange={(_e, data) => {
                    setUsageRestriction(
                      data.value as DatasetUsageRestriction | '',
                    );
                  }}
                  disabled={isSubmitting}
                >
                  {DATASET_USAGE_RESTRICTION_OPTIONS.map(({ label, value }) => (
                    <option key={label} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Sub Type */}
              <Field label="Sub Type">
                <Input
                  className={styles.input}
                  placeholder="e.g. meetings-v2"
                  autoComplete="off"
                  value={subType}
                  onChange={(_e, data) => setSubType(data.value)}
                  disabled={isSubmitting}
                />
              </Field>

              {/* Source */}
              <Field label="Source">
                <Input
                  className={styles.input}
                  placeholder="e.g. teams, sharepoint"
                  autoComplete="off"
                  value={source}
                  onChange={(_e, data) => setSource(data.value)}
                  disabled={isSubmitting}
                />
              </Field>

              {/* Retention */}
              <Field
                label="Retention"
                hint="Dataset data and files inherit this retention window after creation."
              >
                <Select
                  aria-label="Retention"
                  className={styles.dropdown}
                  value={retentionOption}
                  onChange={(_e, data) => {
                    setRetentionOption(data.value as DatasetRetentionOption);
                  }}
                  disabled={isSubmitting}
                >
                  {DATASET_RETENTION_OPTIONS.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Source Properties */}
              <Field
                label="Source Properties"
                hint="JSON object"
                validationState={sourcePropertiesError ? 'error' : 'none'}
                validationMessage={sourcePropertiesError ?? undefined}
              >
                <Textarea
                  aria-label="Source Properties"
                  className={styles.textarea}
                  value={sourceProperties}
                  onChange={(_e, data) => {
                    setSourceProperties(data.value);
                    setSourcePropertiesError(null);
                  }}
                  disabled={isSubmitting}
                  resize="vertical"
                  rows={4}
                />
              </Field>

              {/* Submit error */}
              {submitError !== null && (
                <MessageBar intent="error" className={styles.errorBar}>
                  <MessageBarBody>{submitError}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <Button
              appearance="primary"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
